/**
 * Pure helpers for the venue screen (TV view of a league night).
 */

/** Standings rows shown at once; more rows page every STANDINGS_PAGE_MS. */
export const STANDINGS_PAGE_SIZE = 8;
export const STANDINGS_PAGE_MS = 10000;
/** How often the screen reloads its data. */
export const POLL_MS = 10000;
/** The screen counts as out of date (shows "Reconnecting…") after this long without a refresh. */
export const STALE_MS = 35000;
/** Mouse cursor and controls hide after this long without moving the mouse. */
export const IDLE_MS = 3000;

const pad = (n) => String(n).padStart(2, '0');

/** The viewer's local date as YYYY-MM-DD ("today" at the venue). */
export const localIsoDate = (now = new Date()) =>
  `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

/** "7:05 PM" for 19:05 (a "HH:MM[:SS]" string). */
export const formatGameTime = (time) => {
  const [h, m] = String(time || '').split(':').map(Number);
  if (!Number.isInteger(h) || !Number.isInteger(m)) return 'TBD';
  const hour = h % 12 || 12;
  return `${hour}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`;
};

/** "7:05 PM" for a Date. */
export const formatClock = (date) => formatGameTime(`${date.getHours()}:${date.getMinutes()}`);

/** "Oct 30" for 2026-10-30. */
export const formatShortDate = (iso) => {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** Number of standings pages. */
export const pageCount = (rows, size = STANDINGS_PAGE_SIZE) =>
  Math.max(1, Math.ceil((rows?.length || 0) / size));

/** The rows of standings page `page` (0-based, wraps around). */
export const pageRows = (rows, page, size = STANDINGS_PAGE_SIZE) => {
  const list = rows || [];
  const p = ((page % pageCount(list, size)) + pageCount(list, size)) % pageCount(list, size);
  return list.slice(p * size, p * size + size);
};

/**
 * Standings columns for the TV: few and wide. Follows the league's rules like
 * the standings tab: draws only where games can end level, points unless the
 * table is ranked by wins (then the point difference breaks ties).
 */
export const standingsColumns = (rules) => {
  const byWins = rules?.rank_by === 'wins';
  return [
    { key: 'gp', label: 'P', title: 'Played', value: (t) => t.games_played },
    { key: 'w', label: 'W', title: 'Won', value: (t) => t.wins },
    { key: 'l', label: 'L', title: 'Lost', value: (t) => t.losses },
    ...(rules?.draws_allowed ? [{ key: 'd', label: 'D', title: 'Drawn', value: (t) => t.draws }] : []),
    byWins
      ? {
        key: 'diff',
        label: '+/−',
        title: 'Point difference',
        value: (t) => {
          const diff = (t.points_for || 0) - (t.points_against || 0);
          return diff > 0 ? `+${diff}` : String(diff);
        },
      }
      : { key: 'pts', label: 'Pts', title: 'Points', value: (t) => t.league_points, strong: true },
  ];
};

/** Games scheduled on `date`. */
export const gamesOn = (games, date) => (games || []).filter((g) => g.game_date === date);

/**
 * The leading side of a live match: 'p1' | 'p2' | null. Set sports lead on
 * sets won first, then on points in the current set.
 */
export const leaderOf = (score) => {
  if (!score) return null;
  const sets = score.sets_won;
  if (sets && sets.p1 !== sets.p2) return sets.p1 > sets.p2 ? 'p1' : 'p2';
  const { p1 = 0, p2 = 0 } = score.points || {};
  if (p1 === p2) return null;
  return p1 > p2 ? 'p1' : 'p2';
};

/** "25–20 · 18–25" line of the finished sets. */
export const setLine = (score) =>
  (score?.set_scores || []).map((s) => `${s.p1}–${s.p2}`).join(' · ');

/** The screen route of a league (short code, then UUID, then id). */
export const leagueScreenPath = (league) =>
  `/screen/league/${league?.short_code || league?.shortCode || league?.uuid || league?.id}`;

/** What went wrong loading the screen: 'private' | 'not_found' | 'error'. */
export const screenErrorKind = (error) => {
  const status = error?.response?.status;
  if (status === 403 || status === 401) return 'private';
  if (status === 404 || status === 400) return 'not_found';
  return 'error';
};
